import { escapeHtml } from '../util/escapeHtml.js';
/**
 * Rex Mortis — shared name-suggest typeahead
 *
 * The Attacks-page suggestion mechanic, extracted so EVERY player (or house)
 * name input can offer the same dropdown: debounced fetch, mouse pick,
 * arrow-key + Enter selection, Escape/blur to dismiss.
 *
 * Usage:
 *   attachNameSuggest(input, async (q) => {
 *     const r = await api.suggestTargets(q);
 *     return r.suggestions.map((s) => ({ name: s.name, meta: `Lvl ${s.level}` }));
 *   });
 */

export interface SuggestEntry {
  name: string;
  /** Small grey line shown beside the name (level, location, …). */
  meta: string;
}

const CSS_ID = 'name-suggest-css';
const CSS = `
  .ns-wrap { position: relative; }
  .ns-box {
    position: absolute; left: 0; right: 0; top: 100%; z-index: 40;
    background: var(--c-parchment, #f4ead8); border: 1px solid var(--c-rule, #b8a888);
    border-radius: 3px; box-shadow: 0 6px 18px rgba(20, 12, 4, 0.25);
    max-height: 260px; overflow-y: auto;
  }
  .ns-item {
    display: flex; justify-content: space-between; gap: 12px; align-items: baseline;
    padding: 7px 10px; cursor: pointer; border-bottom: 1px solid var(--c-rule, #b8a888);
  }
  .ns-item:last-child { border-bottom: none; }
  .ns-item:hover, .ns-item.active { background: rgba(180, 140, 40, 0.16); }
  .ns-name { font-family: var(--font-display); color: var(--c-ink); }
  .ns-meta { font-family: var(--font-numeric); font-size: 0.8rem; color: var(--c-ink-soft); }
`;

function ensureCss(): void {
  if (document.getElementById(CSS_ID)) return;
  const style = document.createElement('style');
  style.id = CSS_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

export function attachNameSuggest(
  input: HTMLInputElement,
  fetchFn: (q: string) => Promise<SuggestEntry[]>,
  onChoose?: (name: string) => void,
): void {
  ensureCss();

  // Wrap the input so the dropdown anchors to it.
  const parent = input.parentElement;
  if (!parent) return;
  let wrap = parent;
  if (!wrap.classList.contains('ns-wrap')) {
    wrap = document.createElement('div');
    wrap.className = 'ns-wrap';
    parent.insertBefore(wrap, input);
    wrap.appendChild(input);
  }
  const box = document.createElement('div');
  box.className = 'ns-box';
  box.style.display = 'none';
  wrap.appendChild(box);

  let timer = 0;
  let activeIdx = -1;

  const hide = (): void => {
    box.style.display = 'none';
    box.innerHTML = '';
    activeIdx = -1;
  };
  const choose = (name: string): void => {
    input.value = name;
    hide();
    onChoose?.(name);
  };
  const render = (items: SuggestEntry[]): void => {
    if (items.length === 0) {
      hide();
      return;
    }
    activeIdx = -1;
    box.innerHTML = items
      .map(
        (s) => `
        <div class="ns-item" data-name="${escapeHtml(s.name)}">
          <span class="ns-name">${escapeHtml(s.name)}</span>
          <span class="ns-meta">${escapeHtml(s.meta)}</span>
        </div>`,
      )
      .join('');
    box.style.display = 'block';
    box.querySelectorAll<HTMLDivElement>('.ns-item').forEach((el) => {
      // mousedown (not click) so it fires before the input's blur hides us.
      el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        choose(el.dataset.name!);
      });
    });
  };

  input.addEventListener('input', () => {
    const q = input.value.trim();
    window.clearTimeout(timer);
    if (q.length < 1) {
      hide();
      return;
    }
    timer = window.setTimeout(async () => {
      try {
        const items = await fetchFn(q);
        if (input.value.trim() === q) render(items); // ignore stale responses
      } catch {
        /* suggestions are best-effort */
      }
    }, 160);
  });

  input.addEventListener('keydown', (e) => {
    const rows = box.style.display !== 'none'
      ? Array.from(box.querySelectorAll<HTMLDivElement>('.ns-item'))
      : [];
    if (rows.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      activeIdx =
        e.key === 'ArrowDown'
          ? (activeIdx + 1) % rows.length
          : (activeIdx - 1 + rows.length) % rows.length;
      rows.forEach((r, i) => r.classList.toggle('active', i === activeIdx));
      return;
    }
    if (e.key === 'Enter' && activeIdx >= 0 && rows[activeIdx]) {
      // Swallow the Enter entirely so page-level Enter handlers (submit/add)
      // don't fire against the half-typed value. Attach this component BEFORE
      // any page Enter handler on the same input.
      e.preventDefault();
      e.stopImmediatePropagation();
      choose(rows[activeIdx]!.dataset.name!);
      return;
    }
    if (e.key === 'Escape') hide();
  });

  input.addEventListener('blur', () => {
    window.setTimeout(hide, 120);
  });
}

/** Standard player-name fetcher — wraps the same endpoint the Attacks page uses. */
export async function fetchPlayerSuggestions(q: string): Promise<SuggestEntry[]> {
  const { api } = await import('../api/client.js');
  const r = await api.suggestTargets(q);
  return r.suggestions.map((s) => ({
    name: s.name,
    meta: `Lvl ${s.level} · ${s.status.charAt(0) + s.status.slice(1).toLowerCase()}`,
  }));
}
